import React from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { PageHeader } from '../PageHeader';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { ScreenLoadingOverlay } from '@/components/common/ScreenLoadingOverlay';
import { SettingsProfileCard } from './SettingsProfileCard';
import { SettingsListItem } from './SettingsListItem';
import { SettingsToggleItem } from './SettingsToggleItem';
import { SettingsSection } from './SettingsSection';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface SettingsContentProps {
  displayName: string;
  email: string;
  propertyUnit: string;
  initials: string;
  appVersion: string;
  shouldShowPayments: boolean;
  isSendingVerification: boolean;
  isLoggingOut: boolean;
  isDarkMode: boolean;
  communityUpdatesEnabled: boolean;
  stagedRoommateEmail: boolean;
  stagedRoommatePhone: boolean;
  onProfilePress: () => void;
  onLinkedAccountsPress: () => void;
  onChangePassword: () => void;
  onPaymentMethodsPress: () => void;
  onAutopayPress: () => void;
  onToggleTheme: () => void;
  onToggleCommunityUpdates: (val: boolean) => void;
  onToggleRoommateEmail: (val: boolean) => void;
  onToggleRoommatePhone: (val: boolean) => void;
  onHowItWorksPress: () => void;
  onTutorialPress: () => void;
  onFeedbackPress: () => void;
  onLogout: () => void;
  onDeleteAccountPress: () => void;
  contentPaddingBottom?: number;
}

export const SettingsContent: React.FC<SettingsContentProps> = React.memo(({
  displayName,
  email,
  propertyUnit,
  initials,
  appVersion,
  shouldShowPayments,
  isSendingVerification,
  isLoggingOut,
  isDarkMode,
  communityUpdatesEnabled,
  stagedRoommateEmail,
  stagedRoommatePhone,
  onProfilePress,
  onLinkedAccountsPress,
  onChangePassword,
  onPaymentMethodsPress,
  onAutopayPress,
  onToggleTheme,
  onToggleCommunityUpdates,
  onToggleRoommateEmail,
  onToggleRoommatePhone,
  onHowItWorksPress,
  onTutorialPress,
  onFeedbackPress,
  onLogout,
  onDeleteAccountPress,
  contentPaddingBottom = 40,
}) => {
  const { colors, semantic } = useThemeColors();

  const ICON_COLORS = {
    blue: {
      bg: colors.accent.blueBgLight,
      border: colors.accent.blueBgLight,
    },
    green: {
      bg: colors.accent.greenLight,
      border: colors.accent.greenLight,
    },
    purple: {
      bg: colors.accent.purpleLight,
      border: colors.accent.purpleLight,
    },
    orange: {
      bg: colors.accent.orangeLight,
      border: colors.accent.orangeLight,
    },
    neutral: {
      bg: colors.activityIcon.bg,
      border: colors.activityIcon.border,
    },
    destructive: {
      bg: colors.accent.redBg,
      border: colors.accent.redBg,
    },
  };

  return (
    <View style={styles.wrapper}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: contentPaddingBottom }]}
        showsVerticalScrollIndicator={false}
      >
        <PageHeader title="Settings" />

        {/* Profile Card */}
        <View style={styles.profileCardWrapper}>
          <SettingsProfileCard
            displayName={displayName}
            email={email}
            propertyUnit={propertyUnit}
            initials={initials}
            onPress={onProfilePress}
          />
        </View>

        {/* Account Section */}
        <SettingsSection title="Account">
          <SettingsListItem
            title="Linked Accounts"
            subtitle="Manage connected accounts"
            icon={<RESIDENTS_SVG_COMPONENTS.LINKED_ACCOUNTS width={18} height={18} color={PRIMITIVE_COLORS.resioBlue} />}
            iconBgColor={ICON_COLORS.blue.bg}
            iconBorderColor={ICON_COLORS.blue.border}
            onPress={onLinkedAccountsPress}
          />
          <SettingsListItem
            title="Change Password"
            subtitle="Update your password"
            icon={<RESIDENTS_SVG_COMPONENTS.PADLOCK width={18} height={18} color={PRIMITIVE_COLORS.resioBlue} />}
            iconBgColor={ICON_COLORS.blue.bg}
            iconBorderColor={ICON_COLORS.blue.border}
            onPress={onChangePassword}
            disabled={isSendingVerification}
          />
          {shouldShowPayments && (
            <>
              <SettingsListItem
                title="Payment Methods"
                subtitle="Manage your cards"
                icon={<RESIDENTS_SVG_COMPONENTS.CREDIT_CARD width={18} height={18} color={PRIMITIVE_COLORS.resioBlue} />}
                iconBgColor={ICON_COLORS.blue.bg}
                iconBorderColor={ICON_COLORS.blue.border}
                onPress={onPaymentMethodsPress}
              />
              <SettingsListItem
                title="Autopay"
                subtitle="Manage automatic payments"
                icon={<RESIDENTS_SVG_COMPONENTS.PAY width={18} height={18} color={colors.accent.green} />}
                iconBgColor={ICON_COLORS.green.bg}
                iconBorderColor={ICON_COLORS.green.border}
                onPress={onAutopayPress}
                showSeparator={false}
              />
            </>
          )}
        </SettingsSection>

        {/* Appearance Section */}
        <SettingsSection title="Appearance" useCard>
          <SettingsToggleItem
            label="Dark Mode"
            icon={<RESIDENTS_SVG_COMPONENTS.MOON width={16} height={16} color={PRIMITIVE_COLORS.resioPurple} />}
            iconBgColor={ICON_COLORS.purple.bg}
            iconBorderColor={ICON_COLORS.purple.border}
            value={isDarkMode}
            onValueChange={onToggleTheme}
            showSeparator={false}
          />
        </SettingsSection>

        {/* Notifications Section */}
        <SettingsSection title="Notifications" useCard>
          <SettingsToggleItem
            label="Push Notifications"
            icon={<RESIDENTS_SVG_COMPONENTS.BELL width={16} height={16} color={PRIMITIVE_COLORS.resioPurple} />}
            iconBgColor={ICON_COLORS.purple.bg}
            iconBorderColor={ICON_COLORS.purple.border}
            value={communityUpdatesEnabled}
            onValueChange={onToggleCommunityUpdates}
          />
          <SettingsToggleItem
            label="Community Updates"
            icon={<RESIDENTS_SVG_COMPONENTS.NOTIFICATION width={16} height={16} color={PRIMITIVE_COLORS.resioPurple} />}
            iconBgColor={ICON_COLORS.purple.bg}
            iconBorderColor={ICON_COLORS.purple.border}
            value={communityUpdatesEnabled}
            onValueChange={onToggleCommunityUpdates}
          />
          <SettingsToggleItem
            label="Rent Reminders"
            icon={<RESIDENTS_SVG_COMPONENTS.DOLLAR width={16} height={16} color={PRIMITIVE_COLORS.warningOrange} />}
            iconBgColor={ICON_COLORS.orange.bg}
            iconBorderColor={ICON_COLORS.orange.border}
            value={communityUpdatesEnabled}
            onValueChange={onToggleCommunityUpdates}
          />
          <SettingsToggleItem
            label="Maintenance Updates"
            icon={<RESIDENTS_SVG_COMPONENTS.WRENCH width={16} height={16} color={PRIMITIVE_COLORS.warningOrange} />}
            iconBgColor={ICON_COLORS.orange.bg}
            iconBorderColor={ICON_COLORS.orange.border}
            value={false}
            onValueChange={() => {}}
            showSeparator={false}
          />
        </SettingsSection>

        {/* Privacy Section */}
        <SettingsSection title="Privacy" useCard>
          <SettingsToggleItem
            label="Share Email with Roommates"
            icon={<RESIDENTS_SVG_COMPONENTS.EMAIL_BUBBLE width={16} height={16} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            value={stagedRoommateEmail}
            onValueChange={onToggleRoommateEmail}
          />
          <SettingsToggleItem
            label="Share Phone with Roommates"
            icon={<RESIDENTS_SVG_COMPONENTS.CHAT_BUBBLE width={16} height={16} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            value={stagedRoommatePhone}
            onValueChange={onToggleRoommatePhone}
            showSeparator={false}
          />
        </SettingsSection>

        {/* Support Section */}
        <SettingsSection title="Support">
          <SettingsListItem
            title="How It Works"
            subtitle="Learn about Resio"
            icon={<RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE width={18} height={18} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            onPress={onHowItWorksPress}
          />
          <SettingsListItem
            title="Tutorial"
            subtitle="Walkthrough & tips"
            icon={<RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE width={18} height={18} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            onPress={onTutorialPress}
          />
          <SettingsListItem
            title="Send Feedback"
            subtitle="Report issues or suggest features"
            icon={<RESIDENTS_SVG_COMPONENTS.CHAT_ICON width={18} height={18} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            onPress={onFeedbackPress}
            showSeparator={false}
          />
        </SettingsSection>

        {/* Account Actions */}
        <SettingsSection title="Account Actions" dimTitle>
          <SettingsListItem
            title="Log Out"
            icon={<RESIDENTS_SVG_COMPONENTS.FINISH width={18} height={18} color={colors.text.tertiary} />}
            iconBgColor={ICON_COLORS.neutral.bg}
            iconBorderColor={ICON_COLORS.neutral.border}
            onPress={onLogout}
            showChevron={false}
            disabled={isLoggingOut}
          />
          <SettingsListItem
            title="Delete Account"
            subtitle="Permanently remove your account"
            icon={<RESIDENTS_SVG_COMPONENTS.TRASH width={18} height={18} color={colors.accent.red} />}
            iconBgColor={ICON_COLORS.destructive.bg}
            iconBorderColor={ICON_COLORS.destructive.border}
            onPress={onDeleteAccountPress}
            isDestructive
            showChevron={false}
            showSeparator={false}
          />
        </SettingsSection>

        {/* Version */}
        {appVersion ? (
          <View style={styles.versionContainer}>
            <Text style={[styles.versionText, { color: colors.text.dimmer }]}>{appVersion}</Text>
          </View>
        ) : null}
      </ScrollView>

      <ScreenLoadingOverlay
        visible={isSendingVerification}
        message="Sending..."
        variant="resident"
        textColor={colors.text.primary}
        spinnerColor={semantic.interactive.primary}
      />
    </View>
  );
});

SettingsContent.displayName = 'SettingsContent';

const HP = REDESIGN_SPACING.horizontalPadding;

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
  },
  profileCardWrapper: {
    marginTop: 24,
  },
  versionContainer: {
    paddingTop: 20,
    paddingBottom: 8,
    alignItems: 'center',
  },
  versionText: {
    fontSize: 12,
    fontWeight: '400',
    textAlign: 'center',
  },
});
